import {type RefObject, useCallback, useEffect, useMemo, useRef} from "react";

import {isClient, isServer} from "@/shared/utils";

interface Props {
    onTriggered: (e: Event) => void;
    disableClick?: boolean;
    disableTouch?: boolean;
    disableKeys?: boolean;
    allowAnyKey?: boolean;
    triggerKeys?: Array<string>;
    ignoredSelectors?: Array<string>; // CSS selectors to ignore
    ignoredRefs?: Array<RefObject<HTMLElement | null>>; // Refs to ignore (like wrappedRef)
    eventTarget?: HTMLElement | Document | Window; // Where to listen for events (default: document)
    useCapture?: boolean; // Use capturing phase (default: true, like react-click-outside)
    enableSmartTouch?: boolean; // If true, distinguishes tap from scroll (for inputs with keyboard). Default: false
}

type EventConfigItem = [boolean | undefined, string, EventListener];

/**
 * Hook to detect clicks outside a component (or key presses).
 *
 * Features:
 * - Listens on document/window/custom element (default: document)
 * - Uses capturing phase by default (like react-click-outside)
 * - Supports ignoring elements by CSS selectors or refs
 * - Handles click, touch, and keyboard events
 *
 * @example
 * const ref = useDetectClickOutside({
 *   onTriggered: () => setIsOpen(false),
 *   ignoredSelectors: ['.modal'],
 *   ignoredRefs: [selectRef],
 *   useCapture: true, // default
 * });
 */
export function useDetectClickOutside<T extends HTMLElement = HTMLElement>({
    onTriggered,
    disableClick,
    disableTouch,
    disableKeys,
    allowAnyKey,
    triggerKeys,
    ignoredSelectors = [],
    ignoredRefs = [],
    eventTarget,
    useCapture = true, // Default: capturing phase (like react-click-outside)
    enableSmartTouch = false, // Default: simple touch detection
}: Props): RefObject<T | null> {
    const ref = useRef<T | null>(null);
    // Use null initially to avoid SSR issues with document
    const targetRef = useRef<HTMLElement | Document | Window | null>(eventTarget || (isClient() ? document : null));

    // Track if user is swiping/scrolling (only if smart detection is enabled)
    const isSwiping = useRef(false);
    const touchStartPos = useRef<{x: number; y: number; time: number} | null>(null);

    const keyListener: EventListener = useCallback(
        (e: Event) => {
            const keyEvent = e as KeyboardEvent;
            if (allowAnyKey) {
                onTriggered(e);
            } else if (triggerKeys) {
                if (triggerKeys.includes(keyEvent.key)) {
                    onTriggered(e);
                }
            } else {
                if (keyEvent.key === "Escape") {
                    onTriggered(e);
                }
            }
        },
        [allowAnyKey, triggerKeys, onTriggered],
    );

    const checkAndTrigger = useCallback(
        (target: Node) => {
            if (!ref.current) return false;

            // Check if target is inside any ignored refs
            if (ignoredRefs.length > 0) {
                const isInsideIgnoredRef = ignoredRefs.some((ignoredRef) => {
                    return ignoredRef.current && ignoredRef.current.contains(target);
                });

                if (isInsideIgnoredRef) {
                    return false;
                }
            }

            // Check if target matches any ignored selectors
            if (ignoredSelectors.length > 0 && target instanceof HTMLElement) {
                const isIgnored = ignoredSelectors.some((selector) => {
                    return target.closest(selector) !== null;
                });

                if (isIgnored) {
                    return false;
                }
            }

            // Check if clicking outside
            return !ref.current.contains(target);
        },
        [ignoredSelectors, ignoredRefs],
    );

    const handleTouchStart: EventListener = useCallback(
        (e: Event) => {
            if (!enableSmartTouch) return; // Skip if simple detection

            const touchEvent = e as TouchEvent;
            isSwiping.current = false;
            if (touchEvent.touches.length > 0) {
                touchStartPos.current = {
                    x: touchEvent.touches[0].clientX,
                    y: touchEvent.touches[0].clientY,
                    time: Date.now(),
                };
            }
        },
        [enableSmartTouch],
    );

    const handleTouchMove: EventListener = useCallback(
        (e: Event) => {
            if (!enableSmartTouch) return; // Skip if simple detection
            if (!touchStartPos.current) return;

            const touchEvent = e as TouchEvent;
            if (touchEvent.touches.length > 0) {
                const deltaX = Math.abs(touchEvent.touches[0].clientX - touchStartPos.current.x);
                const deltaY = Math.abs(touchEvent.touches[0].clientY - touchStartPos.current.y);

                // If moved more than 5px, it's a swipe/scroll
                if (deltaX > 5 || deltaY > 5) {
                    isSwiping.current = true;
                }
            }
        },
        [enableSmartTouch],
    );

    const handleTouchEnd: EventListener = useCallback(
        (e: Event) => {
            // Simple detection (default) - just trigger on any touch end outside
            if (!enableSmartTouch) {
                const target = e.target as Node;
                if (checkAndTrigger(target)) {
                    onTriggered(e);
                }
                return;
            }

            // Smart detection - check if it was a swipe
            if (isSwiping.current) {
                touchStartPos.current = null;
                isSwiping.current = false;
                return;
            }

            touchStartPos.current = null;
            const target = e.target as Node;
            if (checkAndTrigger(target)) {
                onTriggered(e);
            }
        },
        [onTriggered, checkAndTrigger, enableSmartTouch],
    );

    const clickListener: EventListener = useCallback(
        (e: Event) => {
            const target = e.target as Node;
            if (checkAndTrigger(target)) {
                onTriggered(e);
            }
        },
        [onTriggered, checkAndTrigger],
    );

    const eventsConfig: Array<EventConfigItem> = useMemo(() => {
        const events: Array<EventConfigItem> = [
            [disableClick, "click", clickListener],
            [disableKeys, "keyup", keyListener],
        ];

        // For smart touch detection (for inputs with keyboard), listen to all touch events
        if (enableSmartTouch) {
            events.push(
                [disableTouch, "touchstart", handleTouchStart],
                [disableTouch, "touchmove", handleTouchMove],
                [disableTouch, "touchend", handleTouchEnd],
            );
        } else {
            // For simple detection (default), only listen to touchend
            events.push([disableTouch, "touchend", handleTouchEnd]);
        }

        return events;
    }, [disableClick, disableTouch, disableKeys, enableSmartTouch, clickListener, handleTouchStart, handleTouchMove, handleTouchEnd, keyListener]);

    // Update targetRef if eventTarget changes (client-side only)
    useEffect(() => {
        if (isClient()) {
            targetRef.current = eventTarget || document;
        }
    }, [eventTarget]);

    useEffect(() => {
        // Skip on server-side
        if (isServer()) return;

        const target = targetRef.current;
        if (!target) return;

        eventsConfig.forEach((eventConfigItem) => {
            const [isDisabled, eventName, listener] = eventConfigItem;

            if (!isDisabled) {
                // Use passive listeners for touch events to improve scroll performance
                const isTouchEvent = eventName.startsWith("touch");
                const options = isTouchEvent ? {passive: true, capture: useCapture} : useCapture;
                target.addEventListener(eventName, listener, options);
            }
        });

        return () => {
            eventsConfig.forEach((eventConfigItem) => {
                const [isDisabled, eventName, listener] = eventConfigItem;

                if (!isDisabled) {
                    const isTouchEvent = eventName.startsWith("touch");
                    const options = isTouchEvent ? {passive: true, capture: useCapture} : useCapture;
                    target.removeEventListener(eventName, listener, options);
                }
            });
        };
    }, [eventsConfig, useCapture]);

    return ref;
}
